import React from 'react'
import { HelpCircle, User, MapPin, Calendar } from 'lucide-react'
import { useThemeColors } from '../../hooks/useThemeColors'

interface ClarificationDialogProps {
  type: string
  question: string
  options?: { id?: string; name?: string; title?: string; label?: string; email?: string; phone?: string; time?: string; facility?: string; hourlyRate?: number; spotsLeft?: number; confidence?: number }[]
  onSelect: (option: { id?: string; name?: string; title?: string; label?: string }) => void
  onCancel: () => void
}

export function ClarificationDialog({
  type,
  question,
  options,
  onSelect,
  onCancel
}: ClarificationDialogProps) {
  const { colors } = useThemeColors()

  const getIcon = () => {
    switch (type) {
      case 'ambiguous_customer':
      case 'customer_not_found':
        return <User className="w-5 h-5" />
      case 'ambiguous_facility':
      case 'facility_not_found':
        return <MapPin className="w-5 h-5" />
      case 'missing_datetime':
      case 'ambiguous_class':
      case 'class_not_found':
        return <Calendar className="w-5 h-5" />
      default:
        return <HelpCircle className="w-5 h-5" />
    }
  }

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        borderColor: colors.border,
        backgroundColor: `${colors.backgroundLight}80`
      }}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0" style={{ color: colors.primary }}>{getIcon()}</div>
        <p className="font-medium" style={{ color: colors.text }}>{question}</p>
      </div>

      {options && options.length > 0 && (
        <div className="space-y-2 mb-4">
          {options.map((option, index) => (
            <button
              key={option.id || index}
              onClick={() => onSelect(option)}
              className="w-full text-left p-3 rounded-lg border hover:bg-white/5 transition-colors"
              style={{ borderColor: colors.border }}
            >
              <div className="font-medium" style={{ color: colors.text }}>
                {option.name || option.title || option.label}
              </div>
              {option.email && (
                <div className="text-sm" style={{ color: colors.textSecondary }}>
                  {option.email}
                </div>
              )}
              {option.phone && (
                <div className="text-sm" style={{ color: colors.textSecondary }}>
                  {option.phone}
                </div>
              )}
              {option.time && (
                <div className="text-sm" style={{ color: colors.textSecondary }}>
                  {new Date(option.time).toLocaleString()}
                </div>
              )}
              {option.facility && (
                <div className="text-sm" style={{ color: colors.textSecondary }}>
                  at {option.facility}
                </div>
              )}
              {option.hourlyRate !== undefined && (
                <div className="text-sm" style={{ color: colors.textSecondary }}>
                  ${option.hourlyRate}/hour
                </div>
              )}
              {option.spotsLeft !== undefined && (
                <div className="text-sm" style={{ color: colors.textSecondary }}>
                  {option.spotsLeft} spots left
                </div>
              )}
              {typeof option.confidence === 'number' && option.confidence > 0 && (
                <div className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                  {option.confidence}% match
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {type === 'customer_not_found' && (
        <button
          onClick={() => onSelect({ createNew: true })}
          className="w-full p-3 rounded-lg border hover:bg-white/5 transition-colors mb-2"
          style={{ borderColor: colors.border }}
        >
          <span style={{ color: colors.primary }}>+ Create new customer</span>
        </button>
      )}

      <button
        onClick={onCancel}
        className="w-full py-2 rounded-lg border font-medium transition-colors"
        style={{ borderColor: colors.border, color: colors.textSecondary }}
      >
        Cancel
      </button>
    </div>
  )
}
